import { z } from 'zod';
import { FormaPagamentoSchema } from '@/features/forma-pagamento/types/FormaPagamento';
import {
    EmpresaFormaPagamentoTipo,
    EmpresaHorarioFuncionamentoDiaSemana,
    EmpresaHorarioFuncionamentoTimezone,
    EmpresaHorarioFuncionamentoTipo,
    EmpresaSlugCatalogoCompraClienteTipo,
    EmpresaSlugCatalogoExibirPreco,
    EmpresaSlugCatalogoTipoRecebimento,
    EmpresaSlugCatalogoTipoUso,
    EmpresaSlugNotificarEmpresa,
} from './enum';

const EmpresaFormaPagamentoSchema = z.object({
    efp_forma_pagamento_idpk: z.number(),
    efp_idpk: z.number(),
    efp_tipo: z.enum(EmpresaFormaPagamentoTipo),
    efp_ultima_alteracao: z.string(),
    forma_pagamento: FormaPagamentoSchema.omit({
        fop_empresa_idpk: true,
        fop_idpk: true,
        fop_ultima_alteracao: true,
    }),
});

export type EmpresaFormaPagamento = z.infer<typeof EmpresaFormaPagamentoSchema>;

const EmpresaHorarioFuncionamentoSchema = z.object({
    dia_semana: z.string(),
    ehf_dia_semana: z.enum(EmpresaHorarioFuncionamentoDiaSemana),
    ehf_empresa_idpk: z.number(),
    ehf_fim: z.string(),
    ehf_idpk: z.number(),
    ehf_inicio: z.string(),
    ehf_timezone: z.enum(EmpresaHorarioFuncionamentoTimezone),
    ehf_tipo: z.enum(EmpresaHorarioFuncionamentoTipo),
});

export type EmpresaHorarioFuncionamento = z.infer<typeof EmpresaHorarioFuncionamentoSchema>;

/** Indica como aceitaremos pedidos no catálogo e se liberaremos cadastro/login pelo catálogo, caso (ems_catalogo_tipo_uso = 2, 3): */
export enum EmpresaSlugCatalogoPedidoClienteModo {
    Todos = 1,
    SomenteLogados = 2,
    SomenteSemCadastro = 3,
}

export enum EmpresaSlugClienteCobrancaSituacao {
    Todas = 1,
    Abertas = 2,
    Pagas = 3,
}

export const EmpresaSlugSchema = z.object({
    catalogo_formas_de_pagamento: z.array(EmpresaFormaPagamentoSchema).nullable(),
    catalogo_horario_funcionamento: z.array(EmpresaHorarioFuncionamentoSchema).nullable(),
    ems_catalogo_ativo: z.enum(['S', 'N']).default('N'),
    ems_catalogo_compra_cliente_tipo: z.enum(EmpresaSlugCatalogoCompraClienteTipo),
    ems_catalogo_entrega_descricao: z.string().nullable(),
    ems_catalogo_entrega_taxa: z.number(),
    ems_catalogo_exibir_preco: z
        .enum(EmpresaSlugCatalogoExibirPreco)
        .default(EmpresaSlugCatalogoExibirPreco.NaoExibir),
    ems_catalogo_notificar_empresa: z.enum(EmpresaSlugNotificarEmpresa),
    ems_catalogo_pagar_recebimento: z.enum(['S', 'N']),
    ems_catalogo_pedido_cliente_modo: z.enum(EmpresaSlugCatalogoPedidoClienteModo),
    ems_catalogo_tipo_recebimento: z.enum(EmpresaSlugCatalogoTipoRecebimento),
    ems_catalogo_tipo_uso: z.enum(EmpresaSlugCatalogoTipoUso),
    ems_cliente_cobranca_ativa: z.enum(['S', 'N']),
    ems_cliente_cobranca_situacao: z.enum(EmpresaSlugClienteCobrancaSituacao),
    ems_consulta_cliente_ativa: z.enum(['S', 'N']).default('N'),
    ems_cor_padrao: z.string().nullable(),
    ems_descricao_texto: z.string().nullable(),
    ems_descricao_titulo: z.string().nullable(),
    ems_email: z.string().nullable(),
    ems_empresa_idpk: z.number(),
    ems_facebook: z.string().nullable(),
    ems_google_maps: z.string().nullable(),
    ems_instagram: z.string().nullable(),
    ems_linkedin: z.string().nullable(),
    ems_sistema_idpk: z.number(),
    ems_slug: z.string().nullable(),
    ems_tabela_preco_idpk: z.number().nullable(),
    ems_twitter: z.string().nullable(),
    ems_whatsapp: z.string().nullable(),
    ems_youtube: z.string().nullable(),
});

export type EmpresaSlug = z.infer<typeof EmpresaSlugSchema>;
